import type { FilterItineraryOption } from '@ops/entities/operation-item';
import { create } from 'zustand';

// Tiny in-memory cache for itinerary labels (id → FilterItineraryOption),
// so the toolbar's `<ItinerariesFilter>` chip can render a human title
// even when the URL only carries UUIDs (LLM-driven `apply_filters`,
// shared link, browser back/forward).
//
// Populated by:
//   1. `<ItinerariesFilter>`'s pick handler — full options from the
//      tRPC lookup go through `cacheMany`.
//   2. The agent panel adapter on `apply_filters` results — the server
//      ships `result.data.labelHints.itineraries` warmed from the same
//      itinerary-resolution step the executor already runs.
//
// Cache misses fall back to UUID-only chips, which is acceptable as a
// transient state — the consultant can still clear the chip and re-pick
// from the autocomplete. A long-term tRPC `itinerariesByIds` lookup is
// possible if cold starts become common, but it's overkill today.
//
// Not persisted: labels rehydrate on demand from the sources above.

type ItineraryLabelStore = {
  byId: Map<string, FilterItineraryOption>;
  cacheOne: (option: FilterItineraryOption) => void;
  cacheMany: (options: readonly FilterItineraryOption[]) => void;
  clear: () => void;
};

// Producers know different subsets: `itinerariesByIds` carries the travel
// dates, the itinerary-ref resolver and the agent-panel hints only the label.
// Merging (rather than replacing) keeps a field that one producer already
// resolved when a later, blinder one writes `null`/`undefined` over it.
const mergeOption = (
  prev: FilterItineraryOption | undefined,
  next: FilterItineraryOption
): FilterItineraryOption =>
  prev
    ? {
        id: next.id,
        ref: next.ref ?? prev.ref,
        title: next.title ?? prev.title,
        country: next.country ?? prev.country,
        startTime: next.startTime ?? prev.startTime,
        endTime: next.endTime ?? prev.endTime,
      }
    : next;

export const useItineraryLabelStore = create<ItineraryLabelStore>((set) => ({
  byId: new Map(),
  cacheOne: (option) =>
    set((state) => {
      const next = new Map(state.byId);
      next.set(option.id, mergeOption(state.byId.get(option.id), option));
      return { byId: next };
    }),
  cacheMany: (options) =>
    set((state) => {
      if (options.length === 0) return state;
      const next = new Map(state.byId);
      for (const option of options) {
        next.set(option.id, mergeOption(next.get(option.id), option));
      }
      return { byId: next };
    }),
  clear: () => set({ byId: new Map() }),
}));

// Public read-only selectors — hide the internal Map shape from consumers.
// Use these instead of `.byId.get(id)` / `.byId.size` so tests and components
// don't couple to the internal structure.
export const getLabel = (id: string): FilterItineraryOption | undefined =>
  useItineraryLabelStore.getState().byId.get(id);

export const labelCount = (): number =>
  useItineraryLabelStore.getState().byId.size;
